// Badge unlock logic and tracking utilities

// Badge definitions with unlock conditions
export const BADGE_DEFINITIONS = [
  {
    id: 'early-bird',
    name: '🌅 Şafak Bekçisi',
    description: '3+ sabah etkinliğine katıldı (08:00 öncesi)',
    icon: 'Sunrise',
    target: 3,
    condition: 'morning_events'
  },
  {
    id: 'night-owl',
    name: '🌕 Gece Kurdu',
    description: '3+ gece etkinliğine katıldı',
    icon: 'Moon',
    target: 3,
    condition: 'night_events'
  },
  {
    id: 'winter-warrior',
    name: '❄️ Kış Savaşçısı',
    description: 'Kış aylarında etkinliklere katıldı',
    icon: 'Snowflake',
    target: 1,
    condition: 'winter_events'
  },
  {
    id: 'explorer',
    name: '🗺️ Kaşif',
    description: '3+ farklı lokasyonda etkinliğe katıldı',
    icon: 'MapPin',
    target: 3,
    condition: 'unique_locations'
  },
  {
    id: 'carpool-hero',
    name: '🚗 Yol Arkadaşı',
    description: '5+ araç paylaşımı ilanı oluşturdu',
    icon: 'Car',
    target: 5,
    condition: 'carpool_offers'
  },
  {
    id: 'eco-warrior',
    name: '♻️ Doğa Dostu',
    description: 'Admin tarafından çevre katkısı için ödüllendirildi',
    icon: 'Leaf',
    target: 1,
    condition: 'admin_awarded'
  }
];

// Parse event time from string (e.g., "17:00 - 19:30" or "17:00")
const parseEventTime = (timeString) => {
  if (!timeString) return null;
  const timeMatch = timeString?.match(/(\d{1,2}):(\d{2})/);
  if (!timeMatch) return null;
  return parseInt(timeMatch?.[1], 10);
};

// Parse event date to get month
const parseEventMonth = (dateString) => {
  if (!dateString) return null;
  // Try to parse various date formats
  const date = new Date(dateString);
  if (isNaN(date)) return null;
  return date?.getMonth() + 1; // 1-12
};

// Check if event is in winter months (December, January, February)
const isWinterEvent = (dateString) => {
  const month = parseEventMonth(dateString);
  return month === 12 || month === 1 || month === 2;
};

// Check if event is a morning event (before 08:00)
const isMorningEvent = (timeString) => {
  const hour = parseEventTime(timeString);
  return hour !== null && hour < 8;
};

// Check if event is a night event (20:00 or later)
const isNightEvent = (timeString) => {
  const hour = parseEventTime(timeString);
  return hour !== null && hour >= 20;
};

// Calculate badge progress for a user
export const calculateBadgeProgress = (userId) => {
  const bookings = JSON.parse(localStorage.getItem('bookings') || '[]');
  const events = JSON.parse(localStorage.getItem('publishedEvents') || '[]');
  const listings = JSON.parse(localStorage.getItem('listings') || '[]');
  const users = JSON.parse(localStorage.getItem('users') || '[]');
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  
  // Find user data
  const targetUserId = userId || currentUser?.id;
  const userData = users?.find(u => u?.id === targetUserId) || currentUser;
  
  // Get user's confirmed bookings
  const userBookings = bookings?.filter(b => b?.userId === targetUserId);
  
  // Get events user attended (with completed status)
  const attendedEventIds = userBookings?.map(b => b?.eventId);
  const attendedEvents = events?.filter(e => 
    attendedEventIds?.includes(e?.id) && e?.status === 'completed'
  );
  
  // Calculate morning events count
  const morningEventsCount = attendedEvents?.filter(e => isMorningEvent(e?.time))?.length || 0;
  
  // Calculate night events count
  const nightEventsCount = attendedEvents?.filter(e => isNightEvent(e?.time))?.length || 0;
  
  // Calculate winter events count
  const winterEventsCount = attendedEvents?.filter(e => isWinterEvent(e?.date))?.length || 0;
  
  // Calculate unique locations
  const uniqueLocations = [...new Set(attendedEvents?.map(e => e?.location)?.filter(Boolean))];
  const uniqueLocationsCount = uniqueLocations?.length || 0;
  
  // Calculate carpool offers (user's carpool listings)
  const userCarpoolOffers = listings?.filter(l => 
    l?.userId === targetUserId && l?.type === 'carpool'
  )?.length || 0;
  
  // Check if eco warrior badge is admin-awarded
  const ecoWarriorAwarded = userData?.badges?.some(b => b?.id === 'eco-warrior') || false;
  
  return {
    morning_events: morningEventsCount,
    night_events: nightEventsCount,
    winter_events: winterEventsCount,
    unique_locations: uniqueLocationsCount,
    carpool_offers: userCarpoolOffers,
    admin_awarded: ecoWarriorAwarded ? 1 : 0
  };
};

// Generate achievement objects with progress
export const generateAchievements = (userId) => {
  const progress = calculateBadgeProgress(userId);
  const users = JSON.parse(localStorage.getItem('users') || '[]');
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const targetUserId = userId || currentUser?.id;
  const userData = users?.find(u => u?.id === targetUserId) || currentUser;
  
  return BADGE_DEFINITIONS?.map(badge => {
    const currentProgress = progress?.[badge?.condition] || 0;
    const isUnlocked = currentProgress >= badge?.target;
    
    // Check if badge was previously unlocked (stored in user data)
    const existingBadge = userData?.achievements?.find(a => a?.id === badge?.id);
    const unlockedDate = existingBadge?.unlockedDate || (isUnlocked ? new Date()?.toLocaleDateString('tr-TR') : null);
    
    return {
      id: badge?.id,
      name: badge?.name,
      description: badge?.description,
      icon: badge?.icon,
      unlocked: isUnlocked,
      progress: currentProgress,
      target: badge?.target,
      unlockedDate: unlockedDate
    };
  });
};

// Update user achievements in localStorage
export const updateUserAchievements = (userId) => {
  const achievements = generateAchievements(userId);
  const users = JSON.parse(localStorage.getItem('users') || '[]');
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const targetUserId = userId || currentUser?.id;
  
  // Update in users array
  const updatedUsers = users?.map(user => {
    if (user?.id === targetUserId) {
      return { ...user, achievements };
    }
    return user;
  });
  localStorage.setItem('users', JSON.stringify(updatedUsers));
  
  // Update currentUser if it's the target
  if (currentUser?.id === targetUserId) {
    const updatedCurrentUser = { ...currentUser, achievements };
    localStorage.setItem('currentUser', JSON.stringify(updatedCurrentUser));
  }
  
  return achievements;
};

// Award badge manually (admin action)
export const awardBadgeManually = (userId, badgeId) => {
  const users = JSON.parse(localStorage.getItem('users') || '[]');
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  
  const badgeDefinition = BADGE_DEFINITIONS?.find(b => b?.id === badgeId);
  if (!badgeDefinition) return false;
  
  const updatedUsers = users?.map(user => {
    if (user?.id === userId) {
      const achievements = user?.achievements || generateAchievements(userId);
      const updatedAchievements = achievements?.map(achievement => {
        if (achievement?.id === badgeId) {
          return {
            ...achievement,
            unlocked: true,
            progress: achievement?.target,
            unlockedDate: new Date()?.toLocaleDateString('tr-TR')
          };
        }
        return achievement;
      });
      return { ...user, achievements: updatedAchievements };
    }
    return user;
  });
  
  localStorage.setItem('users', JSON.stringify(updatedUsers));
  
  // Update currentUser if it's the target
  if (currentUser?.id === userId) {
    const updatedUser = updatedUsers?.find(u => u?.id === userId);
    if (updatedUser) {
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    }
  }
  
  return true;
};

// Revoke badge manually (admin action)
export const revokeBadgeManually = (userId, badgeId) => {
  const users = JSON.parse(localStorage.getItem('users') || '[]');
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  
  const updatedUsers = users?.map(user => {
    if (user?.id === userId) {
      const achievements = user?.achievements || [];
      const updatedAchievements = achievements?.map(achievement => {
        if (achievement?.id === badgeId) {
          // Recalculate actual progress
          const progress = calculateBadgeProgress(userId);
          const currentProgress = progress?.[achievement?.condition] || 0;
          return {
            ...achievement,
            unlocked: false,
            progress: currentProgress,
            unlockedDate: null
          };
        }
        return achievement;
      });
      return { ...user, achievements: updatedAchievements };
    }
    return user;
  });
  
  localStorage.setItem('users', JSON.stringify(updatedUsers));
  
  // Update currentUser if it's the target
  if (currentUser?.id === userId) {
    const updatedUser = updatedUsers?.find(u => u?.id === userId);
    if (updatedUser) {
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    }
  }
  
  return true;
};
